import { atom } from "jotai"
import type { FeedbackQuestion, SentimentQuestion } from "~/types"
import { aDuration, aProgress, aWatchedSeconds } from "./player"

export type FeedbackQuestionState =
  | "not_shown"
  | "shown"
  | "shrunk"
  | "collapsed"
  | "closed"
  | "answered"

export type FeedbackAnswerState = string | string[] | null

export type ThumbsUpDownFeedback = {
  feedback_question_state: FeedbackQuestionState
  feedback_answer_state: FeedbackAnswerState
  feedback_seconds: number
  feedback_version: string
  feedback_question: string
  feedback_question_id: number
}

export const aFeedbackQuestion = atom<FeedbackQuestion>({
  question: "Worth watching?",
  phoneQuestion: "More?",
  desktopQuestion: "Want more like this?",
  questionId: 1,
  options: [{ value: "thumbs_up" }, { value: "thumbs_down" }],
  watched_seconds: 10,
  question_type: "thumbsUpDown",
})

export const aFeedbackReady = atom(false)

export const aFeedbackData = atom<ThumbsUpDownFeedback>({
  feedback_question_state: "not_shown",
  feedback_answer_state: null,
  feedback_seconds: 0,
  feedback_version: "0.0.1",
  feedback_question: "",
  feedback_question_id: 1,
})

export const aShowingFeedback = atom<{
  question: FeedbackQuestion
  index: number
} | null>((get) => {
  const questions = get(aFeedbackQuestion)
  const watchedSeconds = get(aWatchedSeconds)
  const feedbackData = get(aFeedbackData)

  if (!questions) return null

  const terminalStates: FeedbackQuestionState[] = ["answered"]
  if (
    feedbackData &&
    terminalStates.includes(feedbackData.feedback_question_state)
  )
    return null

  if (questions.watched_seconds > watchedSeconds) return null

  return { question: questions, index: 0 }
})

export const aSentimentQuestions = atom<SentimentQuestion[]>([])

// Track answered question indices
export const aAnsweredQuestionIndices = atom<Set<number>>(new Set<number>())

// Map of sentiment question id → { answer, question, status }
export const aSentimentAnswers = atom<Record<number, { answer: string | null; question: string; status: null | "shown" | "answered" }>>({})

// Find the next unanswered question that should block progress
export const aBlockingQuestion = atom<{
  question: SentimentQuestion
  index: number
} | null>((get) => {
  const questions = get(aSentimentQuestions)
  const progress = get(aProgress)
  const answeredIndices = get(aAnsweredQuestionIndices)

  // Find first unanswered question whose start_seconds we've reached or passed
  for (let i = 0; i < questions.length; i++) {
    if (!answeredIndices.has(i) && progress >= questions[i].start_seconds) {
      return { question: questions[i], index: i }
    }
  }
  return null
})

// Maximum allowed seek position (earliest unanswered question's start_seconds)
export const aMaxSeekPosition = atom<number>((get) => {
  const questions = get(aSentimentQuestions)
  const answeredIndices = get(aAnsweredQuestionIndices)
  const duration = get(aDuration)

  // Find earliest unanswered question
  for (let i = 0; i < questions.length; i++) {
    if (!answeredIndices.has(i)) {
      return questions[i].start_seconds
    }
  }
  return duration // No unanswered questions, allow seeking to end
})
